<template>
  <top-navigation previous-route="Group" :title="$t('pages.settings.title')" />

  <div class="mx-auto min-h-screen px-4 pb-8 pt-24">
    <!-- Language Selector -->
    <div class="mb-6 flex w-full items-center justify-end">
      <language-selector />
    </div>

    <!-- Notifications -->
    <information-text
      v-if="!pwaInstalled"
      :information="$t('pages.settings.pwa_notification')"
      class="mb-4"
    />

    <!-- Profile Settings Section -->
    <settings-section :title="$t('pages.setting.profile_settings')" icon="👤">
      <settings-item
        icon="⚙️"
        :label="$t('pages.setting.profile_settings')"
        :show-border="false"
        @click="$router.push({ name: 'groupUserProfileSettings' })"
      />
    </settings-section>

    <!-- Group Settings Section -->
    <settings-section :title="$t('pages.setting.group_settings')" icon="🎯">
      <settings-item
        icon="✏️"
        :label="$t('pages.setting.group_profile_settings')"
        @click="$router.push({ name: 'groupProfileSettings' })"
      />
      <settings-item
        icon="👨‍👩‍👧‍👦"
        :label="$t('pages.setting.member_settings')"
        @click="$router.push({ name: 'memberSettings' })"
      />
      <settings-item
        icon="🔐"
        :label="$t('pages.setting.role_settings')"
        @click="$router.push({ name: 'memberPermissions' })"
      />
      <settings-item
        v-if="!isRunningInFlutter() && isOrganiser"
        icon="📥"
        :label="
          downloadingQuotes
            ? $t('pages.setting.downloading_quotes')
            : $t('pages.setting.download_quotes')
        "
        @click="downloadQuotesExport"
      />
      <settings-item
        icon="🚪"
        :label="$t('pages.setting.leave_group')"
        variant="warning"
        @click="$router.push({ name: 'leaveGroup' })"
      />
      <settings-item
        v-if="isAdmin"
        icon="🗑️"
        :label="$t('pages.setting.delete_group')"
        variant="danger"
        :show-border="false"
        @click="$router.push({ name: 'deleteGroup' })"
      />
    </settings-section>

    <!-- Usage Section -->
    <settings-section
      :title="$t('pages.setting.quota.section_title')"
      icon="📊"
    >
      <settings-item
        icon="📸"
        :label="$t('pages.setting.quota.photo_usage')"
        :value="photoQuotaDisplay"
        @click="openQuotaSheet('photo')"
      />
      <settings-item
        icon="🎬"
        :label="$t('pages.setting.quota.short_usage')"
        :value="shortQuotaDisplay"
        :show-border="false"
        @click="openQuotaSheet('short')"
      />
    </settings-section>

    <!-- Device Settings Section -->
    <settings-section :title="$t('pages.setting.device_settings')" icon="📱">
      <settings-item
        icon="🔔"
        :label="$t('pages.setting.notifications_in_group')"
        @click="$router.push({ name: 'notificationSettingsView' })"
      />
      <settings-item
        v-if="hasSupportForAppReviewFeature()"
        icon="⭐"
        :label="$t('pages.setting.rate_app')"
        @click="openStoreListing()"
      />
      <settings-item
        :icon="mode === 'dark' ? '🌙' : mode === 'light' ? '☀️' : '🌓'"
        :label="$t('pages.setting.dark_mode')"
        :value="
          mode === 'dark'
            ? $t('pages.setting.on')
            : mode === 'light'
              ? $t('pages.setting.off')
              : $t('pages.setting.system_preference')
        "
        :show-chevron="false"
        @click="toggleMode"
      />
      <div
        class="flex w-full items-center gap-3 p-4 font-medium dark:bg-kekkle-dark-light"
      >
        <span class="text-xl">💾</span>
        <span class="flex-1 text-gray-900 dark:text-slate-200">{{
          $t("pages.setting.media_cache")
        }}</span>
        <span class="font-bold text-kekkle-yellow-dark dark:text-kekkle-yellow">
          {{ cacheStats.sizeFormatted }}
        </span>
        <span v-if="!clearingCache" class="text-xl" @click="handleClearCache">
          🗑️
        </span>
      </div>
    </settings-section>

    <!-- Account Section -->
    <section class="mb-8">
      <h2
        class="mb-3 flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-slate-100"
      >
        <span class="text-2xl">🔑</span>
        {{ $t("pages.settings.your_account") }}
      </h2>
      <KekkleCard>
        <!-- Main Email with Change Button -->
        <div class="">
          <div class="flex-1">
            <p class="text-sm font-bold text-gray-500 dark:text-slate-300">
              {{ $t("pages.settings.email") }}
            </p>
            <p
              class="mt-1 flex items-center gap-2 break-all font-medium text-gray-900 dark:text-slate-200"
              @click="showChangeEmailModal = true"
            >
              {{ userEmail }}
              <span class="text-xl">✏️</span>
            </p>
          </div>
        </div>

        <!-- Authentication Providers -->
        <div class="mt-4 space-y-2">
          <p
            class="text-xs font-bold uppercase text-gray-500 dark:text-slate-300"
          >
            {{ $t("pages.setting.auth_providers") }}
          </p>

          <!-- Password Provider -->
          <div
            v-if="hasPasswordAuth"
            class="flex items-start gap-2 rounded-lg border-2 border-black-dark bg-white p-3 dark:bg-kekkle-dark"
          >
            <span class="text-lg">🔐</span>
            <div class="flex-1">
              <p class="text-sm font-bold text-gray-700 dark:text-slate-300">
                {{ $t("pages.setting.email_password") }}
              </p>
              <p class="break-all text-xs text-gray-600 dark:text-slate-400">
                {{ passwordProviderEmail }}
              </p>
            </div>
          </div>

          <!-- Google Provider -->
          <div
            v-if="hasLinkedGoogle"
            class="flex items-start gap-2 rounded-lg border-2 border-black-dark bg-white p-3 dark:bg-kekkle-dark"
          >
            <img
              class="h-5 w-5"
              src="/img/google/g_logo.svg"
              alt="google-logo"
            />
            <div class="flex-1">
              <p class="text-sm font-bold text-gray-700 dark:text-slate-300">
                {{ $t("pages.setting.linked_to_google") }}
              </p>
              <p class="text-xs text-gray-600 dark:text-slate-400">
                {{ googleProviderEmail }}
              </p>
            </div>
          </div>

          <!-- Apple Provider -->
          <div
            v-if="hasLinkedApple"
            class="flex items-start gap-2 rounded-lg border-2 border-black-dark bg-white p-3 dark:bg-kekkle-dark"
          >
            <img
              class="h-5 w-5"
              src="/img/apple/apple-logo.png"
              alt="apple-logo"
            />
            <div class="flex-1">
              <p class="text-sm font-bold text-gray-700 dark:text-slate-300">
                {{ $t("pages.setting.linked_to_apple") }}
              </p>
              <p class="text-xs text-gray-600 dark:text-slate-400">
                {{ appleProviderEmail }}
              </p>
            </div>
          </div>
        </div>

        <button
          class="mt-4 flex w-full items-center gap-3 rounded-lg border-2 border-black-dark bg-kekkle-red/20 p-3 text-left font-bold transition-all hover:bg-kekkle-red/30 active:translate-x-[1px] active:translate-y-[1px] dark:bg-kekkle-red/30 dark:hover:bg-kekkle-red/40"
          @click="$router.push({ name: 'deleteAccountInfo' })"
        >
          <span class="text-xl">⚠️</span>
          <span
            class="flex-1 text-kekkle-red-dark dark:text-kekkle-red-light"
            >{{ $t("pages.setting.delete_account") }}</span
          >
          <span class="text-kekkle-red">›</span>
        </button>
      </KekkleCard>
    </section>

    <!-- Footer Links -->
    <settings-section :title="$t('pages.settings.help_and_info')" icon="ℹ️">
      <settings-item
        icon="🐦"
        :label="`${$t('pages.setting.questions_twitter_to')} @kekkle_app`"
        @click="openTwitter"
      />
      <settings-item
        icon="🔒"
        :label="$t('auth.terms.privacyPolicy')"
        @click="$router.push({ name: 'PrivacyPolicy' })"
      />
      <settings-item
        icon="📄"
        :label="$t('auth.terms.eula')"
        :show-border="false"
        @click="$router.push({ name: 'Eula' })"
      />
      <div class="break-all border-t-2 border-black-dark p-4">
        <p class="text-xs font-medium text-gray-600 dark:text-slate-400">
          {{ $t("pages.setting.group_id") }}: {{ groupDocId }}
        </p>
        <p class="text-xs font-medium text-gray-600 dark:text-slate-400">
          {{ $t("pages.setting.user_id") }}: {{ userId }}
        </p>
      </div>
    </settings-section>

    <!-- Logout Button -->
    <div class="mx-auto mb-8 max-w-xs">
      <default-button color="red" full-width @click="logout()">
        <span class="text-xl">🚪</span>
        {{ $t("global.inputs.labels.logout") }}
      </default-button>
    </div>

    <!-- Change Email Modal -->
    <change-email-modal
      :visible="showChangeEmailModal"
      @close="showChangeEmailModal = false"
      @logout="logout"
    />

    <!-- Quota Bottom Sheet -->
    <quota-bottom-sheet
      v-model:open="showQuotaSheet"
      :quota="
        quotaSheetType === 'short'
          ? quotaStore.shortQuota
          : quotaStore.photoQuota
      "
      :loading="quotaStore.loading"
      :title="
        quotaSheetType === 'short'
          ? $t('pages.setting.quota.short_title')
          : $t('pages.setting.quota.title')
      "
      :empty-text="
        quotaSheetType === 'short'
          ? $t('pages.setting.quota.short_no_usage')
          : $t('pages.setting.quota.no_usage')
      "
      :delete-info-text="
        quotaSheetType === 'short'
          ? $t('pages.setting.quota.short_delete_info')
          : $t('pages.setting.quota.delete_info')
      "
    />
  </div>
</template>
<script>
import TopNavigation from "../components/TopNavigation.vue";
import { useDarkModeStore } from "@/stores/modules/darkmode/darkmode";
import InformationText from "../components/InformationText.vue";
import SettingsSection from "../components/ui/SettingsSection.vue";
import SettingsItem from "../components/ui/SettingsItem.vue";
import LanguageSelector from "../components/ui/LanguageSelector.vue";
import DefaultButton from "../components/Buttons/DefaultButton.vue";
import ChangeEmailModal from "../components/Modals/ChangeEmailModal.vue";
import { httpsCallable } from "firebase/functions";
import {
  firebaseAnalytics,
  firebaseFunctions,
} from "@/helpers/firebase/firebase";
import { setErrorToast, setSuccessToast } from "@/helpers/notifications/toast";
import * as Sentry from "@sentry/vue";
import { logEvent } from "firebase/analytics";
import {
  isRunningInFlutter,
  hasSupportForAppReviewFeature,
} from "@/helpers/flutter/capabilities";
import { openStoreListing } from "@/helpers/flutter/features/in-app-review";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import { useShortsStore } from "@/stores/modules/shorts/shorts";
import { usePhotosStore } from "@/stores/modules/photos/photos";
import KekkleCard from "@/components/ui/KekkleCard.vue";
import QuotaBottomSheet from "@/components/QuotaBottomSheet.vue";
import {
  getMediaCacheStats,
  clearAllCachedMedia,
} from "@/helpers/cache/image-cache";
import { useQuotaStore } from "@/stores/modules/quota/quota";
import { PencilAltIcon, PencilIcon } from "@heroicons/vue/outline";

export default {
  name: "SettingsView",
  components: {
    InformationText,
    TopNavigation,
    SettingsSection,
    SettingsItem,
    LanguageSelector,
    DefaultButton,
    ChangeEmailModal,
    KekkleCard,
    QuotaBottomSheet,
  },
  setup() {
    const authStore = useAuthStore();
    const groupsStore = useGroupsStore();
    const userStore = useUserStore();
    const darkModeStore = useDarkModeStore();
    const quotaStore = useQuotaStore();
    const shortsStore = useShortsStore();
    const photosStore = usePhotosStore();
    return {
      authStore,
      groupsStore,
      userStore,
      darkModeStore,
      quotaStore,
      shortsStore,
      photosStore,
    };
  },
  data() {
    return {
      mode: "",
      pwaInstalled: true,
      downloadingQuotes: false,
      showChangeEmailModal: false,
      cacheStats: {
        count: 0,
        size: 0,
        sizeFormatted: "0 MB",
      },
      clearingCache: false,
      showQuotaSheet: false,
      quotaSheetType: "photo",
    };
  },
  computed: {
    photoQuotaDisplay() {
      if (!this.quotaStore.photoQuota) return "";
      return `${this.quotaStore.photoQuota.daily_count} / ${this.quotaStore.photoQuota.daily_limit}`;
    },
    shortQuotaDisplay() {
      if (!this.quotaStore.shortQuota) return "";
      return `${this.quotaStore.shortQuota.daily_count} / ${this.quotaStore.shortQuota.daily_limit}`;
    },
    isAdmin() {
      return this.groupsStore.isAdmin;
    },
    isOrganiser() {
      return this.groupsStore.isOrganiser;
    },
    groupDocId() {
      return this.userStore.activeGroup;
    },
    userId() {
      return this.authStore.authUuid;
    },
    userEmail() {
      if (this.authStore.firebaseAuthUser.email) {
        return this.authStore.firebaseAuthUser.email;
      }
      return "";
    },
    hasLinkedGoogle() {
      let providers = this.authStore.firebaseAuthUser?.providerData;
      if (providers) {
        for (let i = 0; i < providers.length; i++) {
          if (providers[i]["providerId"] === "google.com") {
            return true;
          }
        }
      }
      return false;
    },
    hasLinkedApple() {
      let providers = this.authStore.firebaseAuthUser?.providerData;
      if (providers) {
        for (let i = 0; i < providers.length; i++) {
          if (providers[i]["providerId"] === "apple.com") {
            return true;
          }
        }
      }
      return false;
    },
    hasPasswordAuth() {
      let providers = this.authStore.firebaseAuthUser?.providerData;
      if (providers) {
        for (let i = 0; i < providers.length; i++) {
          if (providers[i]["providerId"] === "password") {
            return true;
          }
        }
      }
      return false;
    },
    passwordProviderEmail() {
      let providers = this.authStore.firebaseAuthUser?.providerData;
      if (providers) {
        for (let i = 0; i < providers.length; i++) {
          if (providers[i]["providerId"] === "password") {
            return providers[i].email || "";
          }
        }
      }
      return "";
    },
    googleProviderEmail() {
      let providers = this.authStore.firebaseAuthUser?.providerData;
      if (providers) {
        for (let i = 0; i < providers.length; i++) {
          if (providers[i]["providerId"] === "google.com") {
            return providers[i].email || "";
          }
        }
      }
      return "";
    },
    appleProviderEmail() {
      let providers = this.authStore.firebaseAuthUser?.providerData;
      if (providers) {
        for (let i = 0; i < providers.length; i++) {
          if (providers[i]["providerId"] === "apple.com") {
            return providers[i].email || "";
          }
        }
      }
      return "";
    },
  },
  created() {
    this.pwaInstalled = !!(
      window.navigator.standalone ||
      window.matchMedia("(display-mode: standalone)").matches ||
      document.referrer.includes("android-app://nl.kekkle.app.twa") ||
      isRunningInFlutter()
    );

    if (localStorage.theme === "light") {
      this.mode = "light";
    } else if (localStorage.theme === "dark") {
      this.mode = "dark";
    } else {
      this.mode = "auto";
    }
  },
  async mounted() {
    await this.loadCacheStats();
    if (this.$route.query.showQuota === "true") {
      this.quotaSheetType =
        this.$route.query.type === "shorts" ? "short" : "photo";
      this.showQuotaSheet = true;
      const { showQuota, type, ...rest } = this.$route.query;
      this.$router.replace({ query: rest });
    }
  },
  methods: {
    openStoreListing,
    isRunningInFlutter,
    hasSupportForAppReviewFeature,
    openQuotaSheet(type = "photo") {
      this.quotaSheetType = type;
      this.showQuotaSheet = true;
      this.quotaStore.fetchQuota();
    },
    async loadCacheStats() {
      try {
        this.cacheStats = await getMediaCacheStats();
      } catch (error) {
        console.error("Failed to load cache stats:", error);
        this.cacheStats = {
          count: 0,
          size: 0,
          sizeFormatted: "0 MB",
        };
      }
    },
    async handleClearCache() {
      this.clearingCache = true;
      try {
        await clearAllCachedMedia();
        this.shortsStore.revokeMediaUrls();
        this.photosStore.revokeImageUrls();
        setSuccessToast({
          message: this.$t("global.toast.media_cache_cleared"),
        });
        await this.loadCacheStats();
        logEvent(firebaseAnalytics, "settings", {
          name: "clear_cache",
        });
      } catch (error) {
        Sentry.captureException(error);
        setErrorToast({
          message: this.$t("global.toast.media_cache_clear_failed"),
        });
      } finally {
        this.clearingCache = false;
      }
    },
    async downloadQuotesExport() {
      this.downloadingQuotes = true;
      try {
        const downloadQuotesCsv = httpsCallable(
          firebaseFunctions,
          "downloadQuotesCsv",
        );
        let result = await downloadQuotesCsv({
          groupId: this.groupsStore.groupDocId,
        });
        let csv = result.data;
        let hiddenElement = document.createElement("a");
        hiddenElement.href = "data:text/csv;charset=utf-8," + encodeURI(csv);
        hiddenElement.target = "_blank";
        hiddenElement.download = `${this.$t(
          "pages.setting.quotes_export_filename",
          {
            groupName: this.groupsStore.groupData?.name ?? "",
          },
        )}.csv`;
        hiddenElement.click();
      } catch (e) {
        Sentry.captureException(e);
        setErrorToast({ message: this.$t("global.toast.export_failed") });
      } finally {
        this.downloadingQuotes = false;
      }
    },
    openTwitter() {
      window.open("https://twitter.com/Kekkle_app", "_blank");
    },
    logout() {
      this.authStore.logout();
    },
    toggleMode() {
      switch (this.mode) {
        case "light":
          this.darkModeStore.setDarkMode();
          this.mode = "dark";
          break;
        case "dark":
          this.darkModeStore.setAutoMode();
          this.mode = "auto";
          break;
        case "auto":
          this.darkModeStore.setLightMode();
          this.mode = "light";
          break;
      }
      logEvent(firebaseAnalytics, "settings", {
        name: "toggle_dark_mode",
        mode: this.mode,
      });
    },
  },
};
</script>
